import { createSelector } from '@reduxjs/toolkit';
import { QueryStatus } from '@reduxjs/toolkit/query';
import isEqual from 'lodash/isEqual';
import { DateTime } from 'luxon';
import { createSelectorCreator, lruMemoize } from 'reselect';
import { getResolvedTimesheetAssistant } from '~domains/ai/predictions/redux/predictions.selectors';
import { getTicMemoryDate } from '~redux/modules/tic/tic.legacy-memories.selectors';
import { getTimelyApiSlice } from '~redux/modules/timely/api-slice/timely.api.selector';
import { timelyApi } from '~redux/modules/timely/api-slice/timely.apiSlice';
import type { ListTimesheetProposalsApiResponse } from '../../../../redux/modules/timely/api-slice/GENERATED_ARTIFACT';
import { getCurrentAccountId } from '../../../../redux/selectors/accounts';
import { getUserId } from '../../../../redux/selectors/user';
import type { ProposalActivity, TimesheetEntry } from '../types/types.TimesheetProposals';
import {
  generateIntervalSlots,
  type TimeRange,
  transformProposalToTimesheetEntries,
} from '../utils/util.timesheetTransformers';
import {
  DEFAULT_QUERY_PARAMS,
  getFullProjects,
  getIsAcceptingTimesheet,
  getIsLoadingSelectedProjects,
  getIsRevertingTimesheet,
} from './tic.timesheetProposals.slice';

// Create a selector creator that uses deep equality for result comparison
// This prevents unnecessary re-renders when the transformed data is structurally identical
const createDeepEqualSelector = createSelectorCreator(lruMemoize, isEqual);

export const getIsPatchingTimesheetProposalEntry = createSelector(
  [getTimelyApiSlice],
  timelyApiState =>
    Object.values(timelyApiState.mutations).some(
      mutation =>
        mutation?.status === QueryStatus.pending &&
        mutation.endpointName === 'patchTimesheetProposalEntry'
    )
);

export const getTimesheetProposalsQueryParamsExtended = createSelector(
  [getResolvedTimesheetAssistant, getCurrentAccountId, getUserId, getTicMemoryDate],
  (timesheetAssistant, timelyAccountId, timelyUserId, date) => {
    // Use resolved workspace preferences (admin-enforced → user workspace → system defaults)
    const interval_minutes =
      timesheetAssistant.interval_minutes ?? DEFAULT_QUERY_PARAMS.interval_minutes;
    const provider = timesheetAssistant.provider ?? DEFAULT_QUERY_PARAMS.provider;
    const effort = timesheetAssistant.effort ?? DEFAULT_QUERY_PARAMS.effort;
    const note_language = timesheetAssistant.note_language ?? DEFAULT_QUERY_PARAMS.note_language;

    const params = {
      ...DEFAULT_QUERY_PARAMS,
      interval_minutes,
      provider,
      effort,
      note_language,
      timelyAccountId,
      timelyUserId,
      dateFrom: date,
      dateTo: date,
      page: 1,
      limit: 1,
    };
    return params;
  }
);

export const getTimesheetProposalsRTKQueryBase = createSelector(
  [getTimelyApiSlice, getTimesheetProposalsQueryParamsExtended],
  (localState, queryParams) => {
    // Rails `listTimesheetProposals` derives the account from the request context, so the
    // arg (and therefore the cache key) carries only the list filters — matching the
    // trimmed shape `useInitializeTimesheetProposalsQuery` passes to `.useQuery(...)`.
    return timelyApi.endpoints.listTimesheetProposals.select({
      timelyUserId: queryParams.timelyUserId,
      dateFrom: queryParams.dateFrom,
      dateTo: queryParams.dateTo,
      page: queryParams.page,
      limit: queryParams.limit,
    })({
      [timelyApi.reducerPath]: localState,
    });
  }
);

/**
 * Get the month date range (start of month to end of month) for the current date
 */
const getMonthDateRange = createSelector([getTicMemoryDate], currentDate => {
  if (!currentDate) {
    return { startDate: '', endDate: '' };
  }
  const date = DateTime.fromISO(currentDate);
  const startOfMonth = date.startOf('month');
  const endOfMonth = date.endOf('month');
  return {
    startDate: startOfMonth.toISODate() || '',
    endDate: endOfMonth.toISODate() || '',
  };
});

/**
 * Get RTK Query base for timesheet proposals for the current month
 */
const getTimesheetProposalsForMonthRTKQueryBase = createSelector(
  [getTimelyApiSlice, getMonthDateRange, getCurrentAccountId, getUserId],
  (localState, { startDate, endDate }, timelyAccountId, timelyUserId) => {
    if (!startDate || !endDate || !timelyAccountId || !timelyUserId) {
      return null;
    }
    // `timelyAccountId` gates the query (above) but is not part of the arg/cache key —
    // Rails derives the account from the request context. Keep this arg byte-identical to
    // the month `.useQuery(...)` in `useInitializeTimesheetProposalsQuery` so the cache key matches.
    return timelyApi.endpoints.listTimesheetProposals.select({
      timelyUserId,
      dateFrom: startDate,
      dateTo: endDate,
      page: 1,
      limit: 50, // Enough to cover a full month
    })({
      [timelyApi.reducerPath]: localState,
    });
  }
);

type TimesheetProposalListItem = ListTimesheetProposalsApiResponse['proposals'][0];

/**
 * One row of the `generation_requests[]` sidecar on the LIST response.
 * Generation/attempt state lives off `proposal.status` — this durable,
 * account/user/date-scoped record is what makes the spinner reload-survivable.
 */
export type TimesheetGenerationRequest = NonNullable<
  ListTimesheetProposalsApiResponse['generation_requests']
>[number];

/**
 * Is this generation attempt still "generating" from the user's POV?
 *
 * Only `running` counts. Every terminal status must render the idle state so
 * the Generate button returns: `timed_out` means the attempt was abandoned
 * (engine callback lost, slot freed for retry) — painting it as a spinner
 * would spin forever with no retry control on screen. `failed` gets its red
 * toast from the live Pusher failure arm, and `completed` (incl.
 * empty-success) is signaled by the proposal row / Pusher empty arm.
 * Kept strictly off `proposal.status`.
 */
export const isTimesheetGenerationRunning = (req: TimesheetGenerationRequest): boolean =>
  req.status === 'running';

/**
 * Fold a `generation_requests[]` sidecar into a date→true map of days still generating
 * (running only), keyed YYYY-MM-DD. Shared by the month selector and the week hook so
 * both derive the reload-survivable spinner set the same way.
 */
export const generatingDatesFromRequests = (
  requests: readonly TimesheetGenerationRequest[] | null | undefined
): Record<string, boolean> => {
  const map: Record<string, boolean> = {};
  for (const req of requests ?? []) {
    if (req.date && isTimesheetGenerationRunning(req)) {
      map[req.date.split('T')[0]] = true;
    }
  }
  return map;
};

/**
 * Get a map of timesheet proposals for the current month, keyed by date (YYYY-MM-DD)
 * Returns the full proposal list item data for easy lookup
 * Only includes dates that have proposals - missing dates won't be in the map
 */
export const getTimesheetProposalsMapForMonth = createSelector(
  [getTimesheetProposalsForMonthRTKQueryBase],
  (queryResult): Record<string, TimesheetProposalListItem> => {
    if (!queryResult?.data?.proposals) {
      return {};
    }

    const map: Record<string, TimesheetProposalListItem> = {};

    queryResult.data.proposals.forEach(proposal => {
      if (proposal.date) {
        // Ensure consistent date format (YYYY-MM-DD)
        const normalizedDate = proposal.date.split('T')[0];
        map[normalizedDate] = proposal;
      }
    });

    return map;
  }
);

/**
 * Date→true map of days that are currently generating, derived from the durable
 * `generation_requests[]` sidecar on the month list read.
 *
 * Reuses the same month cache base as `getTimesheetProposalsMapForMonth`, so the
 * sidecar refreshes automatically on every LIST invalidation (a Pusher generation
 * event always invalidates `['TimesheetProposals']`). OR'd into the existing
 * in-memory `generatingDates` flag at read time — the durable row keeps the spinner
 * after the optimistic flag is lost (reload / second device). Dates normalized to
 * YYYY-MM-DD to match the slice's date-keyed `generatingDates`.
 */
export const getGeneratingDatesFromSidecar = createSelector(
  [getTimesheetProposalsForMonthRTKQueryBase],
  (queryResult): Record<string, boolean> =>
    generatingDatesFromRequests(queryResult?.data?.generation_requests)
);

/**
 * Get the proposal ID for the current day from the proposals map
 */
export const getTimesheetProposalIdForCurrentDay = createSelector(
  [getTimesheetProposalsMapForMonth, getTicMemoryDate],
  (proposalsMap, currentDate) => {
    if (!currentDate) {
      return null;
    }
    const normalizedDate = currentDate.split('T')[0]; // Remove time if present
    return proposalsMap[normalizedDate]?.id || null;
  }
);

/**
 * Get the full timesheet proposal query result for the current day (includes loading states)
 */
const getTimesheetProposalQueryResultForCurrentDay = createSelector(
  [getTimelyApiSlice, getTimesheetProposalIdForCurrentDay],
  (apiState, proposalId) => {
    if (!proposalId) {
      return null;
    }
    return timelyApi.endpoints.getTimesheetProposal.select(proposalId)({
      [timelyApi.reducerPath]: apiState,
    });
  }
);

/**
 * Get the full timesheet proposal with entries for the current day
 */
export const getFullTimesheetProposalForCurrentDay = createSelector(
  [getTimesheetProposalQueryResultForCurrentDay],
  queryResult => {
    return queryResult?.data?.proposal;
  }
);

export const getIsManualProposalForCurrentDay = createSelector(
  [getFullTimesheetProposalForCurrentDay],
  proposal => (proposal?.metadata as { is_manual?: boolean } | null | undefined)?.is_manual === true
);

/**
 * All items (activities) on the current day's proposal — assigned and unassigned.
 * Use when you need raw item data including their timestamps.
 */
export const getProposalItemsForCurrentDay = createSelector(
  [getFullTimesheetProposalForCurrentDay],
  // Rails serves item ids (and the entry FK) as NUMBERS; this domain is
  // string-keyed, so coerce both to string at this read boundary (mirrors the
  // coercion in transformProposalToTimesheetEntries).
  (proposal): ProposalActivity[] =>
    (proposal?.items ?? []).map(item => ({
      ...item,
      id: String(item.id),
      timesheet_proposal_entry_id:
        item.timesheet_proposal_entry_id == null
          ? item.timesheet_proposal_entry_id
          : String(item.timesheet_proposal_entry_id),
    }))
);

/**
 * Interval minutes the AI used when generating the current day's proposal.
 * Persisted in proposal.metadata.interval_minutes at generation time, so
 * resize/duration step sizes match what the user actually got back — not
 * whatever their current preference happens to be.
 */
export const getProposalGenerationIntervalMinutes = createSelector(
  [getFullTimesheetProposalForCurrentDay],
  (proposal): number => {
    const metadata = proposal?.metadata as { interval_minutes?: number } | undefined;
    return metadata?.interval_minutes ?? DEFAULT_QUERY_PARAMS.interval_minutes;
  }
);

/** One slot per interval_minutes covering the current proposal's local-time day. */
export const getProposalIntervalSlotsForCurrentDay = createSelector(
  [getTicMemoryDate, getProposalGenerationIntervalMinutes],
  (currentDate, intervalMinutes): TimeRange[] => {
    if (!currentDate) return [];
    return generateIntervalSlots(currentDate, intervalMinutes);
  }
);

/**
 * Get transformed timesheet entries for the current day
 * Uses deep equality comparison to preserve reference stability when entries haven't changed
 */
export const getTransformedTimesheetEntries = createDeepEqualSelector(
  [getFullTimesheetProposalForCurrentDay],
  (
    proposal
  ): {
    assignedEntries: TimesheetEntry[];
    unassignedItems: ProposalActivity[];
    editedEntriesCount: number;
  } => {
    const { assignedEntries, unassignedItems } = transformProposalToTimesheetEntries(proposal);
    const editedEntriesCount = assignedEntries.filter(entry => entry.hasRegenerate).length;
    return {
      assignedEntries,
      unassignedItems,
      editedEntriesCount,
    };
  }
);

// Selectors for checking loading states of timesheet operations
export const getTimesheetOperationsLoadingState = createSelector(
  [
    getIsAcceptingTimesheet,
    getIsRevertingTimesheet,
    getIsLoadingSelectedProjects,
    getIsPatchingTimesheetProposalEntry,
  ],
  (isAccepting, isReverting, isLoadingSelectedProjects, isPatchingEntry) => ({
    isAccepting,
    isReverting,
    isPatchingEntry,
    isLoadingProjects: isLoadingSelectedProjects,
    isLoading: isAccepting || isReverting || isPatchingEntry,
    canSubmit: !isAccepting && !isReverting && !isLoadingSelectedProjects && !isPatchingEntry,
  })
);

// Selector to get full projects for timesheet proposal entries by project IDs
export const getFullProjectsByIds = createSelector(
  [getFullProjects, (_state: any, projectIds: number[]) => projectIds],
  (fullProjects, projectIds) => {
    return projectIds.map(id => fullProjects[id]).filter(project => project !== undefined);
  }
);

// Selector to get selected full projects (projects used in the current proposal)
export const getSelectedFullProjects = createSelector([getFullProjects], fullProjects =>
  Object.values(fullProjects)
);

/**
 * Get loading states for the current day's timesheet proposal
 * - isLoading: true only on initial load when there's no cached data (shows skeletons)
 * - isFetching: true on any request including refetches (for subtle loading indicators)
 */
export const getTimesheetProposalLoadingStatesForCurrentDay = createSelector(
  [getTimesheetProposalQueryResultForCurrentDay],
  queryResult => {
    if (!queryResult) {
      return {
        isLoading: false,
        isFetching: false,
      };
    }
    // RTK Query select results use status property
    const isPending = queryResult.status === QueryStatus.pending;
    const isUninitialized = queryResult.status === QueryStatus.uninitialized;
    const hasData = !!queryResult.data;
    return {
      // Only show loading state when there's no cached data (matches RTK Query hook behavior)
      isLoading: (isPending || isUninitialized) && !hasData,
      isFetching: isPending,
    };
  }
);
